// Who sees which row of "Casos a revisar".
//
// Pure on purpose: the queue page and the hub badge have to agree exactly on
// what counts as "yours", and a badge that counts rows the page then hides is
// worse than no badge at all. Kept out of the components so both read the same
// rules and so the rules can be tested without mounting anything.

// Requerimientos waiting for this user to approve them. pqrsd.approve_response
// reviews any dependency; a dependency lead reviews only the dependencies they
// lead — the same rule the backend enforces on approve/reject.
export const selectInbox = (rows, { myId, canApprove, supervisedGroupIds }) =>
  (rows || []).filter(
    (r) =>
      r.submittedBy !== myId &&
      (canApprove || (!!r.groupId && (supervisedGroupIds || []).includes(r.groupId))) &&
      // Nobody named in particular -> anyone who can approve may pick it up.
      (r.reviewerIds.length === 0 || r.reviewerIds.includes(myId))
  );

// Requerimientos this user submitted and is waiting on somebody else for.
export const selectMine = (rows, { myId }) =>
  (rows || []).filter((r) => r.submittedBy === myId);

// Requerimientos whose plazo ciudadano expired, scoped to the dependencies the
// user actually leads. A general supervisor (pqrsd.list) sees all of them; a
// dependency supervisor sees only their own, because the acto de desistimiento
// is decided on the file, and the file belongs to a dependency.
export const selectExpired = (rows, { canSeeAll, supervisedGroupIds }) => {
  const list = rows || [];
  if (canSeeAll) return list;
  const mine = supervisedGroupIds || [];
  if (!mine.length) return [];
  return list.filter((r) => r.groupId && mine.includes(r.groupId));
};

// Recursos de reposición waiting for a decision. Same scope as an expired
// requerimiento — the decision is taken on the file, and the file belongs to a
// dependency — plus any recurso on a PQRSD assigned to this user, whom
// resolve_appeal also lets decide it.
export const selectAppeals = (rows, { canSeeAll, supervisedGroupIds, myId }) => {
  const list = rows || [];
  if (canSeeAll) return list;
  const mine = supervisedGroupIds || [];
  return list.filter(
    (r) =>
      (r.groupId && mine.includes(r.groupId)) ||
      (myId && Array.isArray(r.assigneeIds) && r.assigneeIds.includes(myId))
  );
};

// The value the "sin dependencia" choice carries. A PQRSD the AI never routed
// has no groupId, and only someone who sees every PQRSD can reach one — but
// those are exactly the ones that go unnoticed, so they get their own choice
// rather than being folded into "todas".
export const NO_GROUP = "__none__";

// "Mi dependencia": the default. Someone who works in a dependency opens this
// page to see their dependency's work, not the entity's. It is one choice
// rather than a per-group default because a user can belong to several.
export const MY_GROUPS = "__mine__";

// The dependencies present among the rows the user can see, so the filter never
// offers a choice that resolves to an empty list.
export const groupOptions = (rows) => {
  const byId = new Map();
  let hasUngrouped = false;
  for (const r of rows || []) {
    if (!r.groupId) { hasUngrouped = true; continue; }
    if (!byId.has(r.groupId)) byId.set(r.groupId, r.groupName || r.groupId);
  }
  const options = [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, "es"));
  if (hasUngrouped) options.push({ id: NO_GROUP, name: "Sin dependencia" });
  return options;
};

// Narrows an already-scoped list to one dependency. Separate from selectExpired
// because the two do different jobs: that one decides what a user is allowed to
// see, this one is the user choosing what to look at. A general supervisor is
// allowed everything and still wants to work one dependency at a time.
export const filterByGroup = (rows, groupId, { myGroupIds = [] } = {}) => {
  if (!groupId) return rows || [];
  if (groupId === NO_GROUP) return (rows || []).filter((r) => !r.groupId);
  if (groupId === MY_GROUPS) {
    return (rows || []).filter((r) => r.groupId && myGroupIds.includes(r.groupId));
  }
  return (rows || []).filter((r) => r.groupId === groupId);
};

// The entity-wide picture, for whoever can see it. A supervisor with every
// dependency in view needs the total to mean the total — "3" has to be three
// petitions whose plazo ran out, wherever they sit — and the breakdown says
// where, so the number leads somewhere instead of just alarming.
export const summarizeExpired = (rows) => {
  const list = rows || [];
  const byGroup = new Map();
  for (const r of list) {
    const name = r.groupName || "Sin dependencia";
    byGroup.set(name, (byGroup.get(name) || 0) + 1);
  }
  return {
    total: list.length,
    dependencies: byGroup.size,
    // Worst first: where the pile is.
    breakdown: [...byGroup.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "es")),
  };
};
